/**
 * FleetSkipper Help Center — HelpCenterModule
 *
 * A self-contained help screen for FleetSkipper trial users. Renders 13 sections
 * from helpContent.js: each section shows numbered steps with optional screenshots.
 *
 * Layout:
 *   - Desktop (lg+): two-column — section list (left) + selected section steps (right).
 *   - Mobile (< lg): accordion — tapping a section title expands its steps.
 *
 * Search: the search box filters sections by title / summary / keywords / step text.
 * When a search term is active, all matching sections are shown; in mobile view all
 * matching sections are expanded automatically; the desktop view shows the first match.
 *
 * Images are served from /help/<filename> (public/help/). All images use
 * loading="lazy" so the page stays fast.
 *
 * Navigation uses useSetBackHandler to return to the dashboard when the mobile
 * Back button is tapped — never <a href> for in-app nav.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Search, ChevronDown, ChevronRight, HelpCircle } from 'lucide-react';
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';
import helpContent from './helpContent/index';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Extract all searchable text from a section's steps for filtering. */
function extractStepText(steps) {
  return steps
    .map((step) => step.text)
    .join(' ')
    .toLowerCase();
}

function sectionMatchesTerm(section, term) {
  if (!term) return true;
  if (section.title.toLowerCase().includes(term)) return true;
  if (section.summary.toLowerCase().includes(term)) return true;
  if (section.keywords.some((kw) => kw.includes(term))) return true;
  if (extractStepText(section.steps).includes(term)) return true;
  return false;
}

// ─── Step component ───────────────────────────────────────────────────────────

function HelpStep({ number, text, image }) {
  return (
    <div className="mb-6 last:mb-0">
      <div className="flex gap-3 mb-3">
        <span
          className="flex-none w-7 h-7 rounded-full bg-cyan-600 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5"
          aria-hidden="true"
        >
          {number}
        </span>
        <p className="text-[0.9375rem] text-gray-800 leading-relaxed">{text}</p>
      </div>
      {image && (
        <div className="ml-10 rounded-xl border border-gray-200 overflow-hidden bg-gray-50 shadow-sm">
          <img
            src={`/help/${image}`}
            alt={text}
            loading="lazy"
            className="w-full h-auto block"
          />
        </div>
      )}
    </div>
  );
}

// ─── Section detail (used in desktop right column + mobile expanded) ──────────

function SectionDetail({ section }) {
  return (
    <div data-testid={`help-section-detail-${section.id}`}>
      <h2 className="text-xl font-bold text-gray-900 mb-5">{section.title}</h2>
      <p className="text-sm text-gray-500 mb-6 leading-relaxed">{section.summary}</p>
      {section.steps.map((step, i) => (
        <HelpStep key={i} number={i + 1} text={step.text} image={step.image} />
      ))}
    </div>
  );
}

// ─── Mobile accordion item ────────────────────────────────────────────────────

function MobileAccordionItem({ section, isOpen, onToggle }) {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden mb-3">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-4 bg-white text-left hover:bg-gray-50 transition-colors"
        aria-expanded={isOpen}
        data-testid={`help-accordion-${section.id}`}
      >
        <span className="text-[0.9375rem] font-semibold text-gray-900">{section.title}</span>
        {isOpen ? (
          <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />
        ) : (
          <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 py-5 bg-gray-50 border-t border-gray-200">
          {section.steps.map((step, i) => (
            <HelpStep key={i} number={i + 1} text={step.text} image={step.image} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Module ──────────────────────────────────────────────────────────────────

const HelpCenterModule = ({ setActiveModule }) => {
  // Register a back handler so the mobile Back button returns to dashboard.
  useSetBackHandler(
    useCallback(() => {
      if (setActiveModule) setActiveModule('dashboard');
    }, [setActiveModule])
  );

  const [searchTerm, setSearchTerm] = useState('');

  // Desktop: which section is selected (index into the VISIBLE sections list).
  const [selectedIdx, setSelectedIdx] = useState(0);

  // Mobile: which section is open in the accordion (id string, or null = all closed).
  const [openSectionId, setOpenSectionId] = useState(helpContent[0]?.id ?? null);

  const term = searchTerm.trim().toLowerCase();

  const visibleSections = useMemo(
    () => helpContent.filter((s) => sectionMatchesTerm(s, term)),
    [term]
  );

  // When search changes, reset selections. Derive the new term inline so we
  // don't read the stale visibleSections memo (which reflects the old term).
  const handleSearchChange = (e) => {
    const newTerm = e.target.value.trim().toLowerCase();
    setSearchTerm(e.target.value);
    setSelectedIdx(0);
    const firstMatch = helpContent.find((s) => sectionMatchesTerm(s, newTerm));
    setOpenSectionId(firstMatch?.id ?? null);
  };

  // Toggle the accordion: one section open at a time (tapping the open one closes it).
  const toggleAccordion = (id) => {
    setOpenSectionId((prev) => (prev === id ? null : id));
  };

  const selectedSection = visibleSections[selectedIdx] ?? visibleSections[0];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-4 md:px-6 py-5 md:py-6">
        <div className="max-w-[1100px] mx-auto">

          {/* ── Page header ───────────────────────────────────────── */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-1">
              <HelpCircle className="h-7 w-7 text-cyan-600 shrink-0" aria-hidden="true" />
              <h1 className="text-2xl font-bold text-gray-900">Help &amp; User Guide</h1>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Step-by-step instructions for every part of FleetSkipper — with screenshots.
              Search for a topic or pick a section.
            </p>
          </div>

          {/* ── Search box ────────────────────────────────────────── */}
          <div className="mb-6 relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none"
              aria-hidden="true"
            />
            <input
              type="search"
              placeholder="Search the guide…"
              value={searchTerm}
              onChange={handleSearchChange}
              aria-label="Search the help guide"
              className="w-full pl-9 pr-3 py-2.5 bg-white text-gray-900 text-sm border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all duration-200"
            />
          </div>

          {/* ── No results ────────────────────────────────────────── */}
          {visibleSections.length === 0 && (
            <div className="bg-white shadow rounded-lg px-6 py-10 text-center">
              <p className="text-sm text-gray-500">
                No sections match <span className="font-medium">"{searchTerm}"</span>. Try a different word.
              </p>
            </div>
          )}

          {visibleSections.length > 0 && (
            <>
              {/* ── Mobile layout (< lg): accordion ─────────────── */}
              <div className="lg:hidden" data-testid="help-mobile-view">
                {visibleSections.map((section) => (
                  <MobileAccordionItem
                    key={section.id}
                    section={section}
                    isOpen={openSectionId === section.id}
                    onToggle={() => toggleAccordion(section.id)}
                  />
                ))}
              </div>

              {/* ── Desktop layout (≥ lg): two-column ────────────── */}
              <div
                className="hidden lg:grid lg:grid-cols-[280px_1fr] lg:gap-6"
                data-testid="help-desktop-view"
              >
                {/* Left: section picker */}
                <nav aria-label="Help sections" className="space-y-1">
                  {visibleSections.map((section, idx) => (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => setSelectedIdx(idx)}
                      data-testid={`help-nav-${section.id}`}
                      aria-pressed={idx === selectedIdx}
                      className={`w-full text-left px-4 py-3 rounded-xl text-[0.875rem] font-semibold transition-colors ${
                        idx === selectedIdx
                          ? 'bg-cyan-50 text-cyan-700'
                          : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      {section.title}
                    </button>
                  ))}
                </nav>

                {/* Right: selected section detail */}
                <div className="bg-white shadow rounded-lg border border-gray-200 p-6 min-w-0">
                  {selectedSection && <SectionDetail section={selectedSection} />}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HelpCenterModule;
